/**
 * Core types for the hard-surface modeling subsystem.
 *
 * SceneObject represents user-created geometry (primitives, imported OBJ
 * meshes, library spawns, etc.), distinct from blend-file objects.
 *
 * EditModeState tracks per-vertex selection and the active edit target.
 */

import type { LoopNormals } from "./normalTypes.ts";
import type { RenderTriangleIndices } from "./triangleIndexTypes.ts";

export type { LoopNormals, VertexNormals } from "./normalTypes.ts";
export {
  asLoopNormals,
  asVertexNormals,
  cloneLoopNormals,
  cloneVertexNormals,
} from "./normalTypes.ts";
export type {
  RenderTriangleIndices,
  SharedTriangleIndices,
} from "./triangleIndexTypes.ts";
export {
  asRenderTriangleIndices,
  asSharedTriangleIndices,
  cloneRenderTriangleIndices,
  cloneSharedTriangleIndices,
  EMPTY_RENDER_TRIANGLE_INDICES,
  EMPTY_SHARED_TRIANGLE_INDICES,
} from "./triangleIndexTypes.ts";

// ---------------------------------------------------------------------------
// Polygon topology — Blender-compatible face structure
// ---------------------------------------------------------------------------

/**
 * Polygon topology using the same representation as Blender:
 * face offsets + corner vertices + explicit edges. Supports tris, quads, and
 * ngons uniformly.
 *
 * Face `i` has corners `corners[offsets[i]] .. corners[offsets[i+1]-1]`.
 * Face count = `offsets.length - 1`.
 * Edge count = `edges.length / 2`.
 */
export interface PolygonTopology {
  /** Start index into `corners` for each face. Length = faceCount + 1. */
  offsets: Uint32Array;
  /** Vertex indices ordered per-face. Length = total corners across all faces. */
  corners: Uint32Array;
  /** All edges as flat vertex-index pairs [v0, v1, v0, v1, ...]. Canonical order: v0 < v1. */
  edges: Uint32Array;
  /** Per-corner edge index: `cornerEdges[c]` is the edge index for the edge
   *  from `corners[c]` to `corners[(c+1) % faceSize]`. Same length as `corners`. */
  cornerEdges: Uint32Array;
  /** Per-edge bitfield flags. Length = edges.length / 2. */
  edgeFlags: Uint8Array;
  /**
   * Per-edge crease weight in [0, 1]. Length = edges.length / 2 when present.
   * Omitted (undefined) when every weight would be 0 — the "omit-when-empty"
   * invariant shared with vertex groups and other sparse optional geometry data.
   *
   * Consumed by the Loop subdivision modifier to produce piecewise-smooth
   * surfaces (Hoppe 1994): edge midpoints LERP between smooth Loop and sharp
   * midpoint rules, and incident-crease counts modulate the vertex update
   * rule (standard ↔ crease-vertex ↔ corner).
   *
   * Survives topology-mutating operations via `remapEdgeCreases` in
   * `provenance.ts`, which matches new edges to old ones through
   * `VertexProvenance.parents`. Subsurf itself retopologises, so the field
   * is stripped from `ModifierGeometry` outputs — creases remain a source
   * property.
   */
  edgeCreases?: Float32Array;
}

/** Edge is sharp — prevents auto-smooth normal averaging across this edge. */
export const EDGE_SHARP = 0x01;
/** Edge is a UV seam — splits UV islands along this edge. */
export const EDGE_SEAM = 0x02;
/** Wire edge — not part of any face. */
export const EDGE_WIRE = 0x04;
/** Boundary edge — belongs to exactly 1 face. */
export const EDGE_BOUNDARY = 0x08;

// ---------------------------------------------------------------------------
// Pivot / transform reference
// ---------------------------------------------------------------------------

/** Where transform operations are centered. Applies in both object and edit mode. */
export type PivotReference =
  | "median" // Median point of the current selection
  | "world" // World origin [0,0,0]
  | "active" // First object in selection (object mode) or first selected vertex (edit mode)
  | "individual" // Each selected element transforms around its own origin
  | "cursor"; // The 3D cursor position (Blender's "3D Cursor" pivot)

// ---------------------------------------------------------------------------
// Object kind
// ---------------------------------------------------------------------------

/** Built-in primitive kinds that can be procedurally generated. */
export type PrimitiveKind =
  | "cube"
  | "sphere"
  | "icosphere"
  | "plane"
  | "circle"
  | "torus"
  | "curve"
  | "cylinder"
  | "cone"
  | "empty"
  | "skin_armature";

/** Light object kinds. */
export type LightKind = "point_light" | "directional_light";

/** All object kinds, including imported geometry, lights, mannequins, and image planes. */
export type ObjectKind =
  | PrimitiveKind
  | "imported"
  | LightKind
  | "mannequin"
  | "image_plane";

/**
 * Shading mode for a scene object.
 *  - `"flat"`: All edges sharp (angle = 0°).
 *  - `"smooth"`: Fully smooth (angle = 180°, all edges averaged).
 *  - `"autoSmooth"`: Angle-based threshold — configurable via `autoSmoothAngle` (default 30°).
 */
export type ShadingMode = "flat" | "smooth" | "autoSmooth";

/** Returns true if the given kind represents a light. */
export function isLightKind(kind: ObjectKind): kind is LightKind {
  return kind === "point_light" || kind === "directional_light";
}

/** Returns true if the given scene object is a light. */
export function isLightObject(obj: SceneObject): boolean {
  return isLightKind(obj.objectKind);
}

/** Returns true if the given scene object is a mannequin. */
export function isMannequinObject(obj: SceneObject): boolean {
  return obj.objectKind === "mannequin";
}

/** Returns true if the given scene object is an empty (no geometry, transform-only helper). */
export function isEmptyObject(obj: SceneObject): boolean {
  return obj.objectKind === "empty";
}

/** Returns true if the given scene object is a curve. */
export function isCurveObject(obj: SceneObject): boolean {
  return obj.objectKind === "curve";
}

/** Returns true if the given scene object is a skin armature (wire mesh for Skin modifier). */
export function isSkinArmatureObject(obj: SceneObject): boolean {
  return obj.objectKind === "skin_armature";
}

/** Returns true if the given scene object is an image plane (textured 2D reference). */
export function isImagePlaneObject(obj: SceneObject): boolean {
  return obj.objectKind === "image_plane";
}

// ---------------------------------------------------------------------------
// Bezier curve data — stored on SceneObject when objectKind is "curve"
// ---------------------------------------------------------------------------

/** Handle constraint type, matching Blender conventions. */
export type CurveHandleType = "auto" | "vector" | "aligned" | "free";

/** A single control point on a Bezier curve in 3D space. */
export interface CurvePoint {
  /** Object-local position of the control point. */
  co: [number, number, number];
  /** Left (incoming) handle position. */
  handleLeft: [number, number, number];
  /** Right (outgoing) handle position. */
  handleRight: [number, number, number];
  /** Handle constraint type for the left handle. */
  handleTypeLeft: CurveHandleType;
  /** Handle constraint type for the right handle. */
  handleTypeRight: CurveHandleType;
  /** Cross-section radius scale at this point (default 1.0). */
  radius: number;
  /** Tilt angle in radians — extra twist applied to the reference frame. */
  tilt: number;
}

/** Bevel (cross-section) settings for generating a tube mesh from the curve. */
export interface CurveBevel {
  /**
   * Whether to generate tube geometry at all. When false the curve renders as
   * a zero-width spline only — matching Blender's default for new curves.
   */
  enabled: boolean;
  /** Radius of the circular cross-section (multiplied by per-point radius). */
  depth: number;
  /** Number of vertices around the cross-section circle. */
  resolution: number;
  /** Whether to fill the start/end caps with fan triangulation. */
  fillCaps: boolean;
}

/** Full Bezier curve definition. */
export interface CurveData {
  /** Ordered control points forming the Bezier spline. */
  points: CurvePoint[];
  /** Number of evaluated samples per Bezier segment. */
  resolutionU: number;
  /** Whether the curve forms a closed loop (last point connects to first). */
  cyclic: boolean;
  /** Bevel settings for tube surface generation. */
  bevel: CurveBevel;
  /**
   * Name of another scene object (a curve) whose shape modulates this
   * curve's radius along its length, Blender-style.
   *
   * The referenced curve's local-space X extent maps to 0→1 along this
   * curve's arc length; at each sample the taper curve's Y value becomes
   * the radius multiplier. Negative values clamp to 0.
   *
   * Resolved by name at geometry-generation time — dangling references
   * (deleted/renamed target) silently no-op, matching the modifier
   * `targetObjectName` pattern.
   */
  taperObjectName?: string | null;
}

// ---------------------------------------------------------------------------
// Material — per-object surface properties
// ---------------------------------------------------------------------------

/**
 * Surface material properties for scene objects.
 *
 * The three required fields (color, roughness, metalness) form the base PBR
 * surface. All other fields are optional — `undefined` means "use default."
 * This keeps old serialized data backward compatible while supporting the
 * full MeshPhysicalMaterial feature set.
 */
export interface SceneMaterial {
  // --- Base PBR (required) ---
  /** Albedo / base color as hex string (e.g. "#c8c8cb") */
  color: string;
  /** Roughness [0–1], where 0 = mirror, 1 = fully diffuse */
  roughness: number;
  /** Metalness [0–1] */
  metalness: number;

  // --- Emissive ---
  /** Emission color as hex string (default "#000000") */
  emissiveColor?: string;
  /** Emission strength (default 0) */
  emissiveIntensity?: number;

  // --- Surface ---
  /** Normal map intensity multiplier (default 1.0) */
  normalIntensity?: number;
  /** Opacity [0–1] (default 1.0 = fully opaque) */
  opacity?: number;
  /** Specular IOR level [0–1] (default 0.5) */
  specularIntensity?: number;

  // --- Coating ---
  /** Clearcoat layer intensity [0–1] (default 0) */
  clearcoat?: number;
  /** Clearcoat roughness [0–1] (default 0) */
  clearcoatRoughness?: number;

  // --- Sheen ---
  /** Sheen intensity [0–1] (default 0) */
  sheen?: number;
  /** Sheen roughness [0–1] (default 1.0) */
  sheenRoughness?: number;
  /** Sheen tint color as hex string (default "#000000") */
  sheenColor?: string;

  // --- World-space surface pattern overlay ---
  /**
   * Surface pattern type. When set, a world-space pattern is rendered over
   * the base color as a perspective and scale reference aid. UV-independent
   * and face-scale independent — cell size is fixed in world units.
   *
   * - `uniform`: regular square grid lines. Toggle H/V lines with `gridLines`.
   * - `brick`: staggered brick-bond pattern. `gridCellSize` is brick height;
   *   width is 2× height. `gridColor` is grout color; `gridGroutThickness`
   *   controls joint width.
   * - `tile`: square tile pattern. `gridCellSize` is tile size. Same grout
   *   controls as brick.
   */
  gridPattern?: "uniform" | "brick" | "tile";
  /** Pattern cell/unit size in meters (default 1 for uniform, 0.075 for brick, 0.3 for tile). */
  gridCellSize?: number;
  /** Pattern opacity [0–1] (default 0.6). */
  gridOpacity?: number;
  /**
   * Primary pattern color as hex string.
   * - `uniform`: grid line color (default "#ffffff").
   * - `brick` / `tile`: grout color (default "#888888").
   */
  gridColor?: string;
  /**
   * Which line directions to render for the `uniform` pattern.
   * - `"both"` (default): horizontal and vertical lines — full grid.
   * - `"h"`: horizontal lines only (constant-V seams).
   * - `"v"`: vertical lines only (constant-U seams).
   * Has no effect on `brick` or `tile` patterns.
   */
  gridLines?: "both" | "h" | "v";
  /**
   * Pattern rotation in degrees. Applies to all pattern types.
   * - `0` (default): no rotation.
   * - `45`: 45° clockwise — uniform grid becomes a diamond/diagonal pattern;
   *   brick and tile courses run diagonally.
   * - `90`: 90° — uniform grid unchanged (symmetric); brick courses become
   *   vertical columns; tile grid unchanged (symmetric).
   */
  gridRotation?: 0 | 45 | 90;
  /**
   * Grout joint half-thickness as a fraction of `gridCellSize` [0–0.5]
   * (default 0.06). Only applies to `brick` and `tile` patterns.
   */
  gridGroutThickness?: number;
  /**
   * Projection mode for the pattern.
   * - `tangent` (default for flat surfaces): surface-tangent projection.
   *   Cells are exactly `gridCellSize` meters along any flat surface,
   *   regardless of orientation. Distorts on smoothly curved geometry.
   * - `triplanar`: world-axis triplanar blend. Cells are symmetric on
   *   curved surfaces (spheres, rounded primitives) with no pole
   *   singularities, but diagonal flat faces show blended projections.
   */
  gridProjection?: "tangent" | "triplanar";
  /**
   * Bump/relief intensity for `brick` and `tile` patterns [0–0.1] (default 0).
   * Adds screen-space normal perturbation to simulate 3D grout channels and
   * raised tile/brick surfaces. Set to 0 (the default) to disable entirely.
   * Values above 0.10 are perceptibly harsh; the slider caps at that maximum.
   * Has no effect on the `uniform` pattern.
   */
  gridBumpScale?: number;

  // --- Glass / Transmission ---
  /** Transmission [0–1] (default 0 = opaque, 1 = fully transmissive) */
  transmission?: number;
  /** Index of refraction (default 1.5) */
  ior?: number;
  /** Volume thickness for transmission (default 0) */
  thickness?: number;
}

/** Default material for newly created primitives. */
export const DEFAULT_MATERIAL: Readonly<SceneMaterial> = {
  color: "#c8c8cb",
  roughness: 0.5,
  metalness: 0.0,
};

// ---------------------------------------------------------------------------
// Material data block — shared, named material in the global registry
// ---------------------------------------------------------------------------

/** A named, reusable material data block. Multiple objects can reference the same block by ID. */
export interface MaterialDataBlock {
  /** Unique identifier (e.g. "mat_1_l1abc") */
  id: string;
  /** User-editable display name (e.g. "Material", "Material.001") */
  name: string;
  /** The actual PBR surface properties */
  properties: SceneMaterial;
  /**
   * When true, keep this material alive even when it has no slot users
   * (Blender-style "fake user"). Prevents automatic orphan cleanup.
   */
  fakeUser?: boolean;
}

let _nextMatId = 1;

/** Generate a unique material data block ID. */
export function createMaterialId(): string {
  return `mat_${_nextMatId++}_${Date.now().toString(36)}`;
}

// ---------------------------------------------------------------------------
// Material slots — Blender-style multi-material per SceneObject
// ---------------------------------------------------------------------------

/**
 * One material slot on a SceneObject. A slot is a stable, user-orderable
 * reference to a `MaterialDataBlock` in the global registry. Two slots on the
 * same object can reference the same material (Blender allows this). The
 * slot's display label in the UI is the referenced material's `name` — there
 * is no separate slot name, so renaming a slot renames the underlying block.
 *
 * A SceneObject always has at least one slot. Slot 0 is the base / fallback.
 * Per-face slot assignment lives on `SceneObject.faceAttributes.materialIndex`;
 * faces without an explicit assignment use slot 0.
 */
export interface MaterialSlot {
  /** Stable identifier (e.g. "mslot_1_xxx"). Never reused; used by ListControl. */
  id: string;
  /** ID of the `MaterialDataBlock` this slot references. */
  materialId: string;
}

let _nextMatSlotId = 1;

/** Generate a unique material slot ID. */
export function createMaterialSlotId(): string {
  return `mslot_${_nextMatSlotId++}_${Date.now().toString(36)}`;
}

/**
 * Build a single-entry `materialSlots` list for a newly constructed
 * SceneObject. Every object must carry at least one slot — use this at all
 * construction sites that previously set `materialId: X`.
 */
export function createDefaultMaterialSlots(materialId: string): MaterialSlot[] {
  return [{ id: createMaterialSlotId(), materialId }];
}

// ---------------------------------------------------------------------------
// Light properties — stored on SceneObject when objectKind is a LightKind
// ---------------------------------------------------------------------------

export interface SceneLightProps {
  /** Color as hex string (e.g. "#ffffff") */
  color: string;
  /** Intensity (arbitrary units — point: ~1–100, directional: ~0.1–3) */
  intensity: number;
}

// ---------------------------------------------------------------------------
// Image plane data — stored on SceneObject when objectKind is "image_plane"
// ---------------------------------------------------------------------------

/**
 * Reference to a textured plane sourced from the local IndexedDB reference-image
 * store. The plane carries no geometry of its own; the renderer builds a unit
 * `THREE.PlaneGeometry` and the `SceneObject.scale` bakes in the image aspect
 * (largest side = 1 unit). Intrinsic pixel dimensions persist so reload can
 * reproduce the same aspect even before the image blob is rehydrated.
 */
export interface ImagePlaneData {
  /** SHA-256 content hash of the source image — key into `referenceImageDB`. */
  imageKey: string;
  /** Source image width in pixels. Used to derive aspect for default scale. */
  intrinsicWidth: number;
  /** Source image height in pixels. */
  intrinsicHeight: number;
}

// ---------------------------------------------------------------------------
// Multi-resolution subdivision mapping
// ---------------------------------------------------------------------------

/**
 * Maps vertices from subdivision level N-1 to level N.
 *
 * Vertices 0..parentVertCount-1 are "even" (1:1 correspondence with parent).
 * Vertices parentVertCount..total-1 are "odd" (edge midpoints).
 * `parentEdges` stores [a0, b0, a1, b1, …] — the two parent vertex indices
 * whose edge midpoint produced each odd vertex.
 */
// ---------------------------------------------------------------------------
// Vertex groups — named sets of shared-vertex indices
// ---------------------------------------------------------------------------

/**
 * Named set of shared-vertex indices. A generic tagging primitive consumed
 * by modifiers and selection tools.
 *
 * Indices refer to `SceneObject.vertices` (stride 3), NOT render-corner
 * indices. Groups are stable across grow topology ops (extrude / subdivide /
 * inset / bevel) and cleared on shrink ops (delete / merge / dissolve) by
 * `reconcilePerVertexAttributes` in `storeEditOps.ts`.
 *
 * Material assignment is NOT a vertex-group concern — see `FaceAttributeTable`
 * for per-face data (material index, shading mode, named face groups).
 */
export interface VertexGroup {
  /** Stable id (e.g. "vg_1_xxxx"). Never reused once removed. Consumers
   *  reference groups by id so that renames do not break references. */
  id: string;
  /** User-editable display name. Not required to be unique — Blender allows
   *  duplicate names and disambiguates by id. */
  name: string;
  /** Shared-vertex indices in the group. */
  indices: Set<number>;
}

// ---------------------------------------------------------------------------
// Face attributes — per-face data (materials, shading, named face groups)
// ---------------------------------------------------------------------------

/**
 * Per-face shading override. Stored as `Uint8Array` for compactness.
 *
 *  - `0` (`FACE_SHADING_INHERIT`): use the object-wide `shading` setting.
 *  - `1` (`FACE_SHADING_FLAT`): force flat (all incident edges sharp).
 *  - `2` (`FACE_SHADING_SMOOTH`): force smooth (all incident edges averaged).
 *  - `3` (`FACE_SHADING_AUTO`): use auto-smooth angle from the object setting.
 *
 * Inherit (0) is the default for unmarked faces. The object-wide setting on
 * `SceneObject.shading` continues to govern those faces.
 */
export const FACE_SHADING_INHERIT = 0;
export const FACE_SHADING_FLAT = 1;
export const FACE_SHADING_SMOOTH = 2;
export const FACE_SHADING_AUTO = 3;

export type FaceShadingValue =
  | typeof FACE_SHADING_INHERIT
  | typeof FACE_SHADING_FLAT
  | typeof FACE_SHADING_SMOOTH
  | typeof FACE_SHADING_AUTO;

/**
 * Named set of face indices ("Face Group"). The face-domain analogue of
 * `VertexGroup`: a recallable selection of faces, persisted on the
 * SceneObject. Used as a saved-selection primitive — distinct from material
 * slots (which are single-valued per face) and from per-face shading.
 *
 * Indices refer to face indices (i.e. `topology.offsets[i]..offsets[i+1]`).
 * Reconciled across topology-mutating ops via `remapFaceGroups` in
 * `provenance.ts`, which uses the op's emitted `faceSources` to project
 * membership forward (a new face is in group X iff *any* of its parents was).
 */
export interface FaceGroup {
  /** Stable id (e.g. "fg_1_xxxx"). Never reused once removed. */
  id: string;
  /** User-editable display name. Not required to be unique. */
  name: string;
  /** Face indices in the group. */
  faces: Set<number>;
}

/**
 * Per-face data table. The face-domain counterpart to per-vertex attributes
 * (`vertexGroups`, `skinRadii`, `baseVertices`).
 *
 * Reserved engine-read entries (`materialIndex`, `shading`) live alongside
 * user-named groups (`groups`) in the same container so the topology-op
 * reconciler can reindex everything in one place.
 *
 * "Omit-when-empty" invariant: every field is omitted when it carries no
 * meaningful data. The whole table is omitted from `SceneObject` when none of
 * its fields would be present.
 */
export interface FaceAttributeTable {
  /**
   * Per-face material slot index. Length = faceCount. Each value is a slot
   * index into `SceneObject.materialSlots`. Omitted when every face uses
   * slot 0 (the dense single-material fast path) or when the object has only
   * one slot.
   */
  materialIndex?: Uint16Array;
  /**
   * Per-face shading override. Length = faceCount. See `FaceShadingValue`.
   * Omitted when every face inherits (i.e. value 0 everywhere) or when no
   * shading overrides exist.
   */
  shading?: Uint8Array;
  /**
   * Named face groups (saved face selections). Omitted when the list is
   * empty.
   */
  groups?: FaceGroup[];
}

// ---------------------------------------------------------------------------
// Scene object — user-created geometry
// ---------------------------------------------------------------------------

export interface SceneObject {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** What kind of object this is */
  objectKind: ObjectKind;
  /** World-space position (Z-up) */
  position: [number, number, number];
  /** Euler rotation in radians (Z-up) */
  rotation: [number, number, number];
  /** Non-uniform scale */
  scale: [number, number, number];
  /**
   * Shared vertex positions as a flat Float32Array (x,y,z repeating).
   * Stored in object-local space. Positions are shared across faces —
   * a cube has 8 vertices, not 24. This matches Blender's BMesh model.
   */
  vertices: Float32Array;
  /**
   * Render-space triangle indices — indexes into expanded (per-corner) arrays,
   * NOT into `vertices` directly. Use `expandForRendering()` to produce the
   * GPU-ready position/normal arrays that these indices reference.
   * Derived from topology via `triangulateTopologyForRendering()`.
   */
  indices: RenderTriangleIndices;
  /**
   * Per-loop (per face-corner) normals. Length = topology.corners.length × 3.
   * Indexed by corner position: `loopNormals[cornerIdx * 3 + c]`.
   * This matches Blender's per-loop normal model — auto-smooth creates
   * different normals at the same vertex for different face corners.
   *
   * Branded type — compile-time distinct from per-vertex normals to prevent
   * buffer-type mismatches (which previously caused WASM traps).
   */
  loopNormals: LoopNormals;
  /** Polygon face topology (quads, ngons — authoritative face structure) */
  topology: PolygonTopology;
  /** Per-corner UV coordinates [u,v, ...] — one pair per topology corner.
   *  Length = topology.corners.length × 2. */
  uvs?: Float32Array;
  /**
   * Optional immutable reference positions for direct sculpt deformation.
   *
   * When present, `vertices` contains sculpted positions while `baseVertices`
   * preserves the pre-sculpt mesh. This keeps direct deformation and
   * sculpt-to-edit transitions anchored to the current mesh.
   */
  baseVertices?: Float32Array;
  /**
   * Per-vertex skin radius scale factors. Length = vertexCount.
   * Each value multiplies mod.depth to produce the effective tube radius for
   * that vertex in the Skin modifier. Default (absent) = 1.0 for all vertices
   * (uniform mod.depth). Values must be > 0.
   */
  skinRadii?: Float32Array;
  /**
   * Named vertex groups — persistent, user-managed sets of shared-vertex
   * indices used as a generic tagging primitive (consumed by modifiers,
   * materials, and selection tools). Omitted when the list is empty. Indices
   * remain valid across grow topology ops (extrude / subdivide / inset /
   * bevel) because those only append new vertices; cleared on shrink ops
   * (delete / merge / dissolve) by `reconcilePerVertexAttributes` because
   * WASM ops do not yet expose a vertexMap.
   */
  vertexGroups?: VertexGroup[];
  /**
   * Monotonic counter bumped whenever this object's vertex positions, vertex
   * count, or face topology changes. Projected sketch points stamp this value
   * into their attachment at creation time; the renderer drops stale points
   * when their host's current `topologyVersion` no longer matches — their
   * `corners` indices are stale.
   *
   * Bumped by every mutator that changes `vertices`, `topology.corners`,
   * `topology.offsets`, or `topology.edges` (e.g. extrude, dissolve,
   * subdivide, sculpt-commit-with-topology-change, edit-mode delete).
   *
   * Optional with implicit-zero semantics: a freshly constructed object that
   * never mutated reads 0. Strokes attached to such an object stamp 0 too,
   * so they survive until something explicitly bumps the version.
   */
  topologyVersion?: number;
  /**
   * Material slots — Blender-style list of references into the global
   * `MaterialDataBlock` registry. Always ≥1 entry. Slot 0 is the default /
   * fallback slot. Per-face slot assignment lives on
   * `faceAttributes.materialIndex`; faces without an explicit assignment use
   * slot 0.
   */
  materialSlots: MaterialSlot[];
  /**
   * Per-face attribute table — material assignment, shading overrides, and
   * named face groups (saved face selections). Omitted when none of its
   * fields would carry meaningful data. Reconciled across topology-mutating
   * ops via `reconcilePerFaceAttributes` in `storeEditOps/core.ts`.
   */
  faceAttributes?: FaceAttributeTable;
  /** Shading mode — see `ShadingMode`. */
  shading: ShadingMode;
  /**
   * Auto-smooth threshold in degrees (default 30, matching Blender 4.x).
   * Only effective when `shading` is `"autoSmooth"`. Edges with a dihedral
   * angle above this threshold are treated as sharp.
   */
  autoSmoothAngle?: number;
  /** Light-specific properties (only present when objectKind is a LightKind) */
  light?: SceneLightProps;
  /** Mannequin model ID (only present when objectKind is "mannequin") */
  mannequinModelId?: string;
  /** Local-space origin offset — applied as a counter-translation on the mesh
   *  so the origin can be repositioned without shifting vertex data (used for
   *  mannequins whose vertex data lives in the GLB). */
  originOffset?: [number, number, number];
  /** Whether this object was imported (e.g. OBJ) and should not be persisted */
  imported?: boolean;
  /** Bezier curve data (only present when objectKind is "curve") */
  curveData?: CurveData;
  /** Image plane data (only present when objectKind is "image_plane") */
  imagePlane?: ImagePlaneData;
  /**
   * Optional single-mesh generator reference — present only on objects
   * produced by a primitive generator whose output is `{ kind: "mesh" }`.
   * Compound generators own their group via `Collection.generatorRef`
   * instead, so their individual children never carry this field.
   *
   * Absent → fully baked, hand-authored-equivalent object.
   * Present → the object stays regenerable from its stored parameters
   * via the generator sidebar section, until the first destructive
   * topology edit clears the ref.
   *
   * See `modeling/generators/types.ts` for the full invariant description.
   */
  generator?: import("./generators/types.ts").GeneratorRef;
}

// ---------------------------------------------------------------------------
// Edit mode
// ---------------------------------------------------------------------------

/** Selection granularity — controls what a click selects and how highlights render. */
export type EditGranularity = "vertex" | "edge" | "face";

export interface EditModeState {
  /** The scene object ID currently being edited */
  objectId: string;
  /** Set of selected vertex indices (into the vertices array, stride 3) */
  selectedVertices: Set<number>;
  /** Explicitly selected edge indices in edge mode (indices into topology.edges).
   *  Authoritative source in edge mode -- prevents shared-vertex inference from
   *  lighting up perpendicular edges that happen to share the same endpoints.
   *  Kept in sync with selectedVertices: edge endpoint vertices are always a
   *  subset of selectedVertices. */
  selectedEdges: Set<number>;
  /** Current selection granularity (defaults to "vertex") */
  granularity: EditGranularity;
  /** The last vertex the user actively clicked (for shortest-path source). */
  lastActiveVertex: number | null;
  /** The last edge the user actively clicked (edge index for edge-mode shortest-path). */
  lastActiveEdge: number | null;
  /** The last face the user actively clicked (for face-mode shortest-path). */
  lastActiveFace: number | null;
  /** Explicitly selected face indices. Authoritative source of truth for
   *  which faces are selected — prevents shared-vertex faces from appearing
   *  selected when only their neighbors were clicked. Kept in sync with
   *  selectedVertices: face vertices are always a subset of selectedVertices,
   *  and selectedFaces always reflects the faces the user actually selected. */
  selectedFaces: Set<number>;
  /** Vertex indices hidden via H key — not rendered, not selectable. Transient (cleared on mode exit). */
  hiddenVertices: Set<number>;
  /** Face indices hidden via H key — not rendered, not selectable. Transient (cleared on mode exit). */
  hiddenFaces: Set<number>;
  /** When true, the next viewport tap triggers extrude/extend at the tap position. */
  extrudePending?: boolean;
  /** Id of the vertex group currently active in the Vertex Groups sidebar panel
   *  (null/undefined = none). Transient edit-mode state; not serialized. */
  activeVertexGroupId?: string | null;
  /** Id of the face group currently active in the Face Groups sidebar panel
   *  (null/undefined = none). Transient edit-mode state; not serialized. */
  activeFaceGroupId?: string | null;
  /** Slot index currently active in the Material panel for face-assignment ops
   *  (null/undefined = none). Transient edit-mode state; not serialized. */
  activeMaterialSlotIndex?: number | null;
  /** For curve objects: flat edit-vertex array [co0, hL0, hR0, co1, hL1, hR1, …] */
  curveEditVertices?: Float32Array;
  /** True when the edited object is a curve. */
  isCurve?: boolean;
  /** X-ray mode — see through mesh, select occluded elements. */
  xray?: boolean;
  /** Tint the mesh surface by vertex group membership. Display-only. */
  showVertexGroupColors?: boolean;
  /** Tint the mesh surface by face group membership. Display-only. */
  showFaceGroupColors?: boolean;
  /** Tint the mesh surface by material slot index. Display-only. */
  showMaterialSlotColors?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let _nextId = 1;

/** Generate a unique scene object ID. */
export function createSceneObjectId(): string {
  return `obj_${_nextId++}_${Date.now().toString(36)}`;
}

let _nextFaceGroupId = 1;

/** Generate a unique face group ID. */
export function createFaceGroupId(): string {
  return `fg_${_nextFaceGroupId++}_${Date.now().toString(36)}`;
}

// ---------------------------------------------------------------------------
// Face attribute helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when every entry in a Uint8Array equals zero. Used to detect
 * the "all faces inherit" state on `FaceAttributeTable.shading`.
 */
function isAllZeroU8(arr: Uint8Array): boolean {
  for (let i = 0; i < arr.length; i++) if (arr[i] !== 0) return false;
  return true;
}

/**
 * Returns true when every entry in a Uint16Array equals zero. Used to detect
 * the "all faces use slot 0" state on `FaceAttributeTable.materialIndex`.
 */
function isAllZeroU16(arr: Uint16Array): boolean {
  for (let i = 0; i < arr.length; i++) if (arr[i] !== 0) return false;
  return true;
}

/**
 * Normalize a `FaceAttributeTable` so it satisfies the omit-when-empty
 * invariant. Returns a fresh table with empty fields removed, or `undefined`
 * if every field is empty.
 *
 * Always call this when assigning `obj.faceAttributes` after a mutation —
 * persistence, diff equality, and renderer fast paths all assume omitted
 * fields are absent rather than zero-filled.
 */
export function normalizeFaceAttributes(
  table: FaceAttributeTable | undefined,
): FaceAttributeTable | undefined {
  if (!table) return undefined;
  const out: FaceAttributeTable = {};
  if (
    table.materialIndex &&
    table.materialIndex.length > 0 &&
    !isAllZeroU16(table.materialIndex)
  ) {
    out.materialIndex = table.materialIndex;
  }
  if (
    table.shading &&
    table.shading.length > 0 &&
    !isAllZeroU8(table.shading)
  ) {
    out.shading = table.shading;
  }
  if (table.groups && table.groups.length > 0) {
    out.groups = table.groups;
  }
  if (
    out.materialIndex === undefined &&
    out.shading === undefined &&
    out.groups === undefined
  ) {
    return undefined;
  }
  return out;
}

/**
 * Look up a face's material slot index. Returns 0 (the base/fallback slot)
 * when no per-face assignment exists. The renderer's `resolveFaceMaterialIndices`
 * uses this same default.
 */
export function getFaceMaterialIndex(
  table: FaceAttributeTable | undefined,
  faceIdx: number,
): number {
  const m = table?.materialIndex;
  if (!m || faceIdx < 0 || faceIdx >= m.length) return 0;
  return m[faceIdx];
}

/**
 * Look up a face's shading override. Returns `FACE_SHADING_INHERIT` when no
 * per-face override exists.
 */
export function getFaceShading(
  table: FaceAttributeTable | undefined,
  faceIdx: number,
): FaceShadingValue {
  const s = table?.shading;
  if (!s || faceIdx < 0 || faceIdx >= s.length) return FACE_SHADING_INHERIT;
  return s[faceIdx] as FaceShadingValue;
}

// ---------------------------------------------------------------------------
// Material slot accessors
// ---------------------------------------------------------------------------

/**
 * Return the primary (slot 0) material id for a SceneObject. This is the
 * correct accessor for any call site that previously read `obj.materialId`
 * and does not care about per-face material assignments — lighting previews,
 * IK solver hints, export defaults, single-material view modes, etc.
 *
 * Primitive objects always carry at least one slot.
 */
export function getPrimaryMaterialId(obj: SceneObject): string {
  return obj.materialSlots[0].materialId;
}

/**
 * Resolve a per-face slot-index array from an object's face attribute table.
 *
 * Returns `undefined` when the object has only one slot, or when no per-face
 * assignment exists (every face resolves to slot 0 — the single-material
 * fast path; the renderer draws one material in one draw call).
 *
 * Out-of-range entries (slot index ≥ `materialSlots.length`) are clamped to
 * slot 0. This is a defensive check for cases where a slot was deleted but
 * the table wasn't yet remapped — the deletion path in `faceAttrOps` should
 * keep things consistent in normal operation.
 *
 * This is the single source of truth for slot→face resolution. Called from
 * the viewport renderer and the path-trace builder; both pass the resulting
 * typed array to `applyFaceMaterialGroups`.
 */
export function resolveFaceMaterialIndices(
  obj: SceneObject,
): Uint16Array | undefined {
  const slotCount = obj.materialSlots.length;
  if (slotCount <= 1) return undefined;

  const stored = obj.faceAttributes?.materialIndex;
  if (!stored || stored.length === 0) return undefined;

  // Defensive clamp: any out-of-range entry (e.g. stale after slot deletion)
  // falls back to slot 0 rather than producing an invalid draw.
  let needsClamp = false;
  for (let i = 0; i < stored.length; i++) {
    if (stored[i] >= slotCount) {
      needsClamp = true;
      break;
    }
  }
  if (!needsClamp) return stored;
  const out = new Uint16Array(stored.length);
  for (let i = 0; i < stored.length; i++) {
    out[i] = stored[i] < slotCount ? stored[i] : 0;
  }
  return out;
}
